import { useCallback, useEffect, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, Input } from 'antd';
import classNames from 'classnames';
import styles from '../../integrations.module.scss';
import { Text } from '@shared/ui/antd';
import type { WebhookIntegrationShortData } from '@shared/api';
import { useFormSchemaUpdate } from '@modules/integrations/hooks/use-form-schema-update';
import type { WebhookIntegrationUpdateFormValues } from '@modules/integrations/lib/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { INTEGRATIONS_MAX_FORM_LIMIT } from '@modules/integrations/lib/constants';
import { IntegrationsItemFields } from './ui/integrations-item-fields';

interface IntegrationItemProps {
  integration: WebhookIntegrationShortData;
  isOpen: boolean;
  isSubmitLoading: boolean;
  onToggle: (id: number) => void;
  onSubmit: (values: WebhookIntegrationUpdateFormValues) => Promise<void>;
}

export const IntegrationItem: FC<IntegrationItemProps> = ({
  integration,
  isOpen,
  isSubmitLoading,
  onToggle,
  onSubmit,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'integrations' });

  const { baseFields } = useFormSchemaUpdate();

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<WebhookIntegrationUpdateFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(baseFields),
    defaultValues: {
      title: '',
      callbackUrl: '',
    },
  });

  const isFormBusy = isSubmitting || isSubmitLoading;
  const isUiLocked = isFormBusy;

  const isSaveDisabled = useMemo(() => {
    if (isFormBusy) return true;
    if (!isDirty) return true;
    return !isValid;
  }, [isDirty, isValid, isFormBusy]);

  useEffect(() => {
    if (isOpen) {
      reset({
        title: integration.title ?? '',

        callbackUrl: (integration as any).callbackUrl ?? (integration as any).callback_url ?? '',
      });
    } else {
      clearErrors();
    }
  }, [isOpen, integration, reset, clearErrors]);

  const handleCancel = useCallback(() => {
    reset({
      title: integration.title ?? '',
      callbackUrl: (integration as any).callbackUrl ?? (integration as any).callback_url ?? '',
    });
    onToggle(integration.id);
  }, [integration, onToggle, reset]);

  const handleSubmitForm = useCallback(
    async (values: WebhookIntegrationUpdateFormValues) => {
      await onSubmit(values);

      reset(values);
      onToggle(integration.id);
    },
    [onSubmit, reset, onToggle, integration.id],
  );

  return (
    <Flex vertical className={classNames(styles.item, { [styles.item_open]: isOpen })}>
      <Form
        onChange={() => clearErrors('root')}
        onFinish={handleSubmit(handleSubmitForm)}
        className={styles.root}
        layout="vertical"
      >
        <Flex justify="space-between" align="center" className={styles.item__header}>
          <Flex vertical gap={4}>
            <Text variant="md" fontWeight={700} className={styles.item__title}>
              {integration.title || t('emptyWebhookTitle')}
            </Text>
            <Text variant="sm" fontWeight={600} className={styles.item__statusText}>
              {t('activeStatus')}
            </Text>
          </Flex>

          {isOpen ? (
            <Flex gap={8} align="center">
              <Button
                htmlType="button"
                variant="text"
                className={styles['item__cancel-button']}
                aria-expanded={isOpen}
                disabled={isUiLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                {t('cancel')}
              </Button>

              <Button
                htmlType="submit"
                variant="text"
                className={styles['item__save-button']}
                aria-expanded={isOpen}
                loading={isFormBusy}
                disabled={isSaveDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {t('save')}
              </Button>
            </Flex>
          ) : (
            <Button
              htmlType="button"
              type="default"
              className={styles.item__editButton}
              aria-expanded={isOpen}
              disabled={isUiLocked}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(integration.id);
              }}
            >
              {t('edit')}
            </Button>
          )}
        </Flex>

        <div className={styles.item__accordion}>
          <div className={styles.item__accordionInner}>
            <Flex vertical gap={16} className={styles.item__placeholder}>
              <Flex className={styles['item__accordion-wrapper']}>
                <Form.Item
                  label={t('titleField')}
                  help={errors.title?.message}
                  validateStatus={errors.title ? 'error' : ''}
                  style={{ width: '100%' }}
                >
                  <Controller
                    control={control}
                    name="title"
                    render={({ field }) => (
                      <Input
                        {...field}
                        showCount
                        maxLength={INTEGRATIONS_MAX_FORM_LIMIT.title}
                        disabled={isUiLocked}
                        placeholder={t('namePlaceholder')}
                        className={styles.item__input}
                      />
                    )}
                  />
                </Form.Item>
              </Flex>

              <Flex className={styles['item__accordion-wrapper']}>
                <Form.Item
                  label={t('callbackUrl')}
                  help={errors.callbackUrl?.message}
                  validateStatus={errors.callbackUrl ? 'error' : ''}
                  style={{ width: '100%' }}
                >
                  <Controller
                    control={control}
                    name="callbackUrl"
                    render={({ field }) => (
                      <Input
                        {...field}
                        showCount
                        maxLength={INTEGRATIONS_MAX_FORM_LIMIT.callbackUrl}
                        disabled={isUiLocked}
                        placeholder={t('callbackUrlPlaceholder')}
                        className={styles.item__input}
                      />
                    )}
                  />
                </Form.Item>
              </Flex>

              <Flex className={styles['item__accordion-wrapper']}>
                <IntegrationsItemFields
                  integrationId={integration.id}
                  isOpen={isOpen}
                  requestMethod={integration.method}
                />
              </Flex>
            </Flex>
          </div>
        </div>
      </Form>
    </Flex>
  );
};
